export interface MaskConfig {
  pattern: string;
  placeholder?: string;
  transform?: (value: string) => string;
}

export type MaskCharType = '#' | 'A' | '*';

export interface MaskCharInfo {
  type: MaskCharType;
  position: number;
}

/**
 * Verifica si un carácter de la máscara es literal (separador)
 * @param char - carácter a verificar
 * @returns true si es un carácter literal
 */
export const isLiteralChar = (char: string): boolean =>
  ['-', ' ', '.', '/', '(', ')', ':', ';', '_'].includes(char);

/**
 * Analiza un patrón de máscara y extrae información sobre los caracteres de entrada
 * @param mask - patrón de máscara (ej: "AA-###-AA")
 * @returns Array con información de cada carácter de entrada
 */
export const analyzeMaskPattern = (mask: string): MaskCharInfo[] => {
  const charInfo: MaskCharInfo[] = [];

  for (let i = 0; i < mask.length; i += 1) {
    const char = mask[i];
    if (char === '#' || char === 'A' || char === '*') {
      charInfo.push({
        type: char as MaskCharType,
        position: i,
      });
    }
  }

  return charInfo;
};

/**
 * Valida si un carácter es válido para un tipo de máscara específico
 * @param char - carácter a validar
 * @param type - tipo de carácter esperado
 * @returns true si el carácter es válido
 */
export const isValidCharForType = (char: string, type: MaskCharType): boolean => {
  switch (type) {
    case '#':
      return /\d/.test(char);
    case 'A':
      return /[a-zA-Z]/.test(char);
    case '*':
      return /[a-zA-Z0-9]/.test(char);
    default:
      return false;
  }
};

/**
 * Extrae solo los caracteres válidos según el patrón de la máscara
 * @param value - valor a procesar
 * @param mask - patrón de máscara
 * @returns Array de caracteres válidos en el orden correcto
 */
export const extractValidChars = (value: string, mask: string): string[] => {
  const charInfo = analyzeMaskPattern(mask);
  const validChars: string[] = [];

  for (let i = 0; i < value.length && validChars.length < charInfo.length; i += 1) {
    const char = value[i];
    // Permitir cualquier carácter alfanumérico (letras y números)
    const isValid = /[a-zA-Z0-9]/.test(char);

    if (isValid) {
      validChars.push(char);
    }
  }

  return validChars;
};

/**
 * Aplica una máscara a un input value
 * @param value - valor a formatear
 * @param mask - máscara a aplicar (ej: "##-########" para teléfono)
 * @returns El valor formateado según la máscara
 */
export const applyMask = (value: string, mask: string): string => {
  if (!value || !mask) {
    return value;
  }

  // Extraer caracteres válidos según el patrón de la máscara
  const validChars = extractValidChars(value, mask);

  let maskedValue = '';
  let valueIndex = 0;

  for (let i = 0; i < mask.length && valueIndex < validChars.length; i += 1) {
    const maskChar = mask[i];

    // Si no es un carácter literal, es una posición válida para que el usuario escriba
    if (!isLiteralChar(maskChar)) {
      // Reemplazar con el siguiente carácter válido (cualquier alfanumérico)
      maskedValue += validChars[valueIndex];
      valueIndex += 1;
    } else {
      // Agregar el carácter literal de la máscara
      maskedValue += maskChar;
    }
  }

  return maskedValue;
};

/**
 * Aplica una máscara y calcula la nueva posición del cursor
 * @param value - El valor a formatear
 * @param mask - La máscara a aplicar
 * @param cursorPosition - La posición actual del cursor
 * @returns Objeto con el valor formateado y la nueva posición del cursor
 */
export const applyMaskWithCursor = (
  value: string,
  mask: string,
  cursorPosition: number,
): { maskedValue: string; newCursorPosition: number } => {
  if (!value || !mask) {
    return { maskedValue: value, newCursorPosition: cursorPosition };
  }

  // Extraer caracteres válidos según el patrón de la máscara
  const validChars = extractValidChars(value, mask);

  // Calcular cuántos caracteres válidos hay antes de la posición del cursor
  const charsBeforeCursor = extractValidChars(value.substring(0, cursorPosition), mask).length;

  let maskedValue = '';
  let valueIndex = 0;
  let newCursorPosition = 0;

  for (let i = 0; i < mask.length && valueIndex < validChars.length; i += 1) {
    const maskChar = mask[i];

    // Si no es un carácter literal, es una posición válida para que el usuario escriba
    if (!isLiteralChar(maskChar)) {
      // Reemplazar con el siguiente carácter válido (cualquier alfanumérico)
      maskedValue += validChars[valueIndex];
      valueIndex += 1;

      // Si este carácter está antes o en la posición del cursor, actualizar la posición
      if (valueIndex <= charsBeforeCursor) {
        newCursorPosition = maskedValue.length;
      }
    } else {
      // Agregar el carácter literal de la máscara
      maskedValue += maskChar;

      // Si estamos escribiendo un carácter válido y el cursor está justo después de un carácter válido,
      // mover el cursor después del carácter literal
      if (valueIndex === charsBeforeCursor && valueIndex > 0) {
        newCursorPosition = maskedValue.length;
      }
    }
  }

  // Si el cursor está en un separador, moverlo a la siguiente posición editable
  if (newCursorPosition < maskedValue.length) {
    const charAtCursor = maskedValue[newCursorPosition];
    if (isLiteralChar(charAtCursor)) {
      // Buscar la siguiente posición editable
      for (let i = newCursorPosition + 1; i < mask.length; i += 1) {
        if (!isLiteralChar(mask[i])) {
          newCursorPosition = i;
          break;
        }
      }
    }
  }
  return { maskedValue, newCursorPosition };
};

/**
 * elimina la máscara de un valor formateado
 * @param maskedValue - El valor con máscara
 * @param mask - La máscara aplicada
 * @returns El valor sin máscara
 */
export const removeMask = (maskedValue: string, mask: string): string => {
  if (!maskedValue || !mask) return maskedValue;

  let cleanValue = '';
  for (let i = 0; i < Math.min(maskedValue.length, mask.length); i += 1) {
    if (mask[i] === '#' || mask[i] === 'A' || mask[i] === '*') {
      cleanValue += maskedValue[i] || '';
    }
  }
  return cleanValue;
};

/**
 * Extrae solo los caracteres válidos de un valor (sin separadores de máscara)
 * @param value - El valor a procesar
 * @returns Solo los caracteres alfanuméricos
 */
export const extractValidCharsOnly = (value: string): string => {
  const result = value.replace(/[^a-zA-Z0-9]/g, '');
  return result;
};

/**
 * Valida si un valor cumple con el patrón de la máscara
 * @param value
 * @param mask
 * @returns true si el valor es válido según la máscara
 */
export const validateMask = (value: string, mask: string): boolean => {
  if (!value || !mask) return true;

  const cleanValue = removeMask(value, mask);
  const charInfo = analyzeMaskPattern(mask);
  const expectedLength = charInfo.length;

  // Solo permitir si la longitud es exactamente la esperada o menor
  return cleanValue.length <= expectedLength;
};

/**
 * Configuraciones predefinidas (ejemplos de uso)
 */
export const MASK_PRESETS = {
  PHONE: '##-########',
  DATE: '##/##/####',
  TIME: '##:##',
  CUSTOM_CODE: 'A#A#-A#A#',
} as const;

export const getMaskPreset = (presetName: keyof typeof MASK_PRESETS): MaskConfig => {
  const pattern = MASK_PRESETS[presetName];
  const placeholder = pattern.replace(/#/g, '_').replace(/A/g, 'A').replace(/\*/g, 'X');

  return {
    pattern,
    placeholder,
  };
};
